import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';
import { OIL_SHOWCASE } from '../data/homeData';
import 'swiper/css';
import 'swiper/css/effect-fade';

function OilsShowcase() {
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const active = OIL_SHOWCASE[activeIndex];
  const total = OIL_SHOWCASE.length;

  const goTo = (index) => {
    const swiper = swiperRef.current;
    if (!swiper) return;
    if (swiper.params.loop) swiper.slideToLoop(index);
    else swiper.slideTo(index);
  };

  return (
    <section className="oils-showcase" id="amma-chekku-oils">
      <div className="oils-showcase-bg" aria-hidden="true" />

      <div className="oils-showcase-inner">
        <header className="reveal oils-showcase-header">
          <div className="oils-showcase-header-text">
            <p className="oils-kicker">Amma Chekku · Own mill</p>
            <h2 className="oils-title">
              Wood-pressed oils,
              <span> made in our mill.</span>
            </h2>
          </div>
          <p className="oils-lede">
            Groundnut, gingelly and coconut — cold-pressed on traditional chekku,
            packed 500&nbsp;ml &amp; 1&nbsp;L for UK wholesale and export.
          </p>
        </header>

        <div className="reveal oils-stage">
          <div className="oils-stage-frame">
            <span className="oils-stage-watermark" aria-hidden="true">
              {String(activeIndex + 1).padStart(2, '0')}
            </span>

            <Swiper
              modules={[Autoplay, EffectFade]}
              className="oils-swiper"
              loop
              effect="fade"
              fadeEffect={{ crossFade: true }}
              speed={900}
              autoplay={{
                delay: 5800,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
            >
              {OIL_SHOWCASE.map((oil) => (
                <SwiperSlide key={oil.id}>
                  <article className="oils-slide">
                    <Link to={oil.href} className="oils-slide-poster" aria-label={oil.name}>
                      <img
                        src={oil.image}
                        alt={oil.name}
                        loading="lazy"
                        decoding="async"
                      />
                    </Link>
                  </article>
                </SwiperSlide>
              ))}
            </Swiper>

            <div className="oils-stage-controls">
              <button
                type="button"
                className="oils-nav-btn"
                aria-label="Previous oil"
                onClick={() => swiperRef.current?.slidePrev()}
              >
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                className="oils-nav-btn"
                aria-label="Next oil"
                onClick={() => swiperRef.current?.slideNext()}
              >
                <ArrowRight size={18} />
              </button>
            </div>
          </div>

          <div className="oils-rail">
            <div className="oils-rail-meta">
              <span>
                {String(activeIndex + 1).padStart(2, '0')}
                <em> / {String(total).padStart(2, '0')}</em>
              </span>
              <span className="oils-rail-active">{active?.title}</span>
            </div>

            <div className="oils-tabs" role="tablist" aria-label="Oil varieties">
              {OIL_SHOWCASE.map((oil, index) => (
                <button
                  key={oil.id}
                  type="button"
                  role="tab"
                  aria-selected={index === activeIndex}
                  className={`oils-tab${index === activeIndex ? ' is-active' : ''}`}
                  onClick={() => goTo(index)}
                >
                  <span className="oils-tab-thumb">
                    <img src={oil.image} alt="" />
                  </span>
                  <span className="oils-tab-label">
                    {oil.title.replace(' Oil', '')}
                  </span>
                </button>
              ))}
            </div>

            <div className="oils-progress" aria-hidden="true">
              <span
                className="oils-progress-bar"
                style={{ width: `${((activeIndex + 1) / total) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default OilsShowcase;
